/**
 * L'atelier graphique — le VÉRIFICATEUR d'une construction (doc 26, § 5).
 *
 * Contrat : `docs/cadre-pedagogique/26-ATELIER-GRAPHIQUE.md`, § 5 (le
 * catalogue, les constats, le verdict) et § 6.4 (sur une mesure, le
 * vérificateur l'emporte sur le modèle).
 *
 * ── Pourquoi ici, et pas dans le CAS ───────────────────────────────────────
 *
 * Fonction PURE, sans dépendance. Elle vit dans le paquet partagé parce que
 * la résolution d'un document est en TypeScript et doit être LA MÊME dans
 * l'atelier, dans la correction et dans le validateur de la génération. La
 * réécrire en Python ferait deux vérités, et c'est la note de l'élève qui
 * arbitrerait entre elles.
 *
 * L'application mobile ne l'appelle JAMAIS contre un attendu : elle ne reçoit
 * pas l'attendu. C'est le serveur qui vérifie, avant P2M (§ 5.5).
 *
 * ── Ce qu'il dit, et ce qu'il ne dit pas ───────────────────────────────────
 *
 * Il rend des CONSTATS — une mesure, un écart, une phrase déterministe et
 * sans jugement — et le VERDICT que ces constats imposent. Il ne rédige
 * aucun commentaire : c'est l'affaire du modèle, qui reçoit ces constats.
 *
 * Un constat `indeterminee` n'est pas un échec de l'élève : c'est le
 * vérificateur qui dit qu'il n'a pas pu mesurer (aucun objet du genre, une
 * référence absente). Il pèse comme un constat non tenu dans le verdict —
 * une construction qu'on ne peut pas mesurer n'est pas une construction
 * juste — mais il se lit autrement dans la copie annotée.
 */
import { jugerCaracteristiques, reduireEsquisse, traitsDeLaFonction, } from './esquisse.js';
import { estIdEnonce, resoudreConstruction, } from './construction.js';
import { aireSignee, asymptotesTangenteV2, boutsPleinsEnConflit, evaluerFonctionV2, lirePartiesV2, familleFonctionV2, fonctionCanoniqueV2, mesureAngle, } from './figure-v2.js';
import { distance, distancePointCercle, distancePointCourbe, formaterNombre, homothetie, milieu, moins, plus, plusProcheSurLigne, refleter, scalaire, tourner, tousFinis, unitaire, } from './geometrie.js';
/**
 * La part de la diagonale du repère qui fait la tolérance de distance par
 * défaut — arbitrage A3, validé le 12 septembre : 1 %, soit ≈ 0,13 unité sur
 * un repère de 10 × 8. CONFIRMÉE par le banc au doigt (lot 8 L3,
 * `docs/pilotage/atelier-figures/L3-BANC-AU-DOIGT.md`) : 95 % des gestes repris
 * sous la loupe tombent à 0,77 % sur tablette.
 */
export const PART_DIAGONALE_TOLERANCE = 0.01;
/**
 * Degrés. 4° depuis le 18 octobre 2026 (décision du fondateur, sur le banc au
 * doigt : 95 % des gestes à 3,9° sur tablette ; 2° était trop serré).
 */
export const TOLERANCE_ANGLE_DEG = 4;
/**
 * Écart relatif. 5 % depuis le 18 octobre 2026 (décision du fondateur, banc au
 * doigt : 4,6 % sur tablette ; 2 % était trop serré).
 */
export const TOLERANCE_RELATIVE = 0.05;
/**
 * La tolérance effective : celle de la partie, complétée par les défauts.
 * La distance se lit sur la DIAGONALE du repère — un repère large tolère
 * plus en valeur absolue, et le geste du doigt garde le même poids à
 * l'écran.
 */
export function toleranceDe(repere, partielle) {
    const diagonale = Math.hypot(repere.xMax - repere.xMin, repere.yMax - repere.yMin);
    const defaut = Number.isFinite(diagonale) && diagonale > 0 ? diagonale : 1;
    const lire = (v, secours) => typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : secours;
    return {
        distance: lire(partielle?.distance, PART_DIAGONALE_TOLERANCE * defaut),
        angleDeg: lire(partielle?.angleDeg, TOLERANCE_ANGLE_DEG),
        relative: lire(partielle?.relative, TOLERANCE_RELATIVE),
    };
}
/** La phase qui livre chaque contrainte (§ 5.1). */
export const PHASE_CONTRAINTE = {
    point_en: 1,
    milieu: 2,
    sur: 2,
    intersection: 2,
    equidistant: 2,
    passe_par: 1,
    perpendiculaire: 2,
    parallele: 2,
    mediatrice: 2,
    bissectrice: 2,
    droite_par: 1,
    pente: 2,
    ordonnee_origine: 2,
    longueur: 1,
    angle: 1,
    rayon: 1,
    aire: 1,
    image: 2,
    isometrique: 2,
    semblable: 2,
    nomme: 1,
    style: 1,
    courbe_egale: 3,
    caracteristiques: 3,
    parties_egales: 3,
    demi_plan: 3,
    vecteur_egal: 3,
    solide: 4,
    marque: 4,
    developpement: 4,
};
/**
 * La phase que ce vérificateur sait mesurer.
 *
 * Elle est INDÉPENDANTE de `PHASE_LIVREE` : le vérificateur doit savoir lire
 * et juger une courbe ou un demi-plan avant que la barre d'outils les
 * propose, sans quoi aucune banque de 4e-5e ne pourrait être validée avant
 * l'ouverture des outils. `PHASE_LIVREE` dit ce que l'application a le droit
 * d'ENVOYER ; celle-ci dit ce que le code sait MESURER.
 */
export const PHASE_VERIFIEE = 3;
const estTypeContrainte = (t) => typeof t === 'string' && Object.prototype.hasOwnProperty.call(PHASE_CONTRAINTE, t);
/* ========================================================== l'outil d'une définition */
/**
 * L'outil qui pose chaque définition — ce que lit `methode.outilsExclus`
 * (arbitrage A2). `image` n'a pas d'outil propre : elle naît de l'outil de sa
 * transformation, et c'est cet objet-là (la rotation, l'homothétie) qui est
 * vu dans le document.
 */
export const OUTIL_PAR_DEFINITION = {
    point_libre: 'point',
    point_sur: 'point',
    milieu: 'milieu',
    intersection: 'intersection',
    segment: 'segment',
    droite: 'droite',
    demi_droite: 'demi_droite',
    perpendiculaire: 'perpendiculaire',
    parallele: 'parallele',
    mediatrice: 'mediatrice',
    bissectrice: 'bissectrice',
    cercle_par_point: 'cercle',
    cercle_rayon: 'cercle',
    cercle_report: 'compas',
    polygone: 'polygone',
    translation: 'translation',
    rotation: 'rotation',
    reflexion: 'reflexion',
    homothetie: 'homothetie',
    image: null,
    angle: 'angle',
    mesure_longueur: 'mesurer',
    mesure_angle: 'mesurer',
    texte: 'texte',
    fonction: 'fonction',
    fonction_par_points: 'fonction',
    vecteur: 'vecteur',
    demi_plan: 'demi_plan',
    table_valeurs: 'table_valeurs',
    esquisse: 'esquisse',
};
/* ================================================================ les outils du calcul */
/** Sous ce seuil, deux points sont confondus et un rapport n'a plus de sens. */
const EPSILON = 1e-9;
/** Le nombre, à la française : « 4,12 ». Jamais arrondi à zéro. */
const nb = (v) => formaterNombre(v);
/** Un écart relatif en pourcentage : « 3,4 % ». */
const pourcent = (v) => `${formaterNombre(v * 100)} %`;
/** Les primes s'écrivent de trois façons ; une seule compte (§ 5.1, `nomme`). */
const PRIME = /['’′]/gu;
const normaliserNom = (n) => n.trim().replace(PRIME, '′');
const nombreDe = (v) => typeof v === 'number' && Number.isFinite(v) ? v : null;
const directionDe = (l) => moins(l.b, l.a);
/**
 * L'angle AIGU (0..90°) entre deux directions non orientées : une droite et
 * son opposée sont la même droite. C'est la seule mesure d'angle qui ne
 * dépend pas du sens dans lequel l'élève a tracé.
 */
function angleDirections(u, v) {
    const a = unitaire(u);
    const b = unitaire(v);
    if (a === null || b === null)
        return null;
    const c = Math.min(1, Math.abs(scalaire(a, b)));
    return (Math.acos(c) * 180) / Math.PI;
}
const angleLignes = (l1, l2) => angleDirections(directionDe(l1), directionDe(l2));
/** L'angle de la pente d'une ligne, dans (−90, 90]. Une verticale vaut 90. */
function angleDePente(l) {
    const d = directionDe(l);
    let a = (Math.atan2(d.y, d.x) * 180) / Math.PI;
    while (a <= -90)
        a += 180;
    while (a > 90)
        a -= 180;
    return a;
}
/**
 * La distance d'un point à un objet. Pour une LIGNE, la sorte compte : « sur »
 * un segment veut dire sur le segment lui-même, pas sur sa droite porteuse
 * (§ 5.1). Pour un polygone, c'est le CONTOUR, pas l'intérieur.
 */
function distanceAObjet(p, o, repere) {
    switch (o.genre) {
        case 'point':
            return distance(p, o.p);
        case 'ligne':
            return plusProcheSurLigne(p, o.a, o.b, o.sorte).d;
        /* Un vecteur est tracé d'un bout à l'autre : « sur un vecteur » veut dire
           sur ce tracé, comme pour un segment. */
        case 'vecteur':
            return plusProcheSurLigne(p, o.de, o.a, 'segment').d;
        /* D'un demi-plan, on mesure la FRONTIÈRE : c'est la seule ligne qu'il
           porte, et « le point est sur la frontière » est ce qu'une consigne
           demande. L'appartenance au demi-plan est l'affaire de `demi_plan`. */
        case 'demi_plan':
            return plusProcheSurLigne(p, o.a, o.b, 'droite').d;
        case 'fonction': {
            const [x0, x1] = domaineTrace(o.domaine, repere);
            if (!(x1 > x0))
                return null;
            return distancePointCourbe(p, (x) => evaluerFonctionV2(o.f, x), x0, x1)?.d ?? null;
        }
        case 'cercle':
            return distancePointCercle(p, o.centre, o.rayon);
        case 'polygone': {
            const n = o.sommets.length;
            if (n < 2)
                return null;
            let d = Infinity;
            for (let i = 0; i < n; i += 1) {
                const a = o.sommets[i];
                const b = o.sommets[(i + 1) % n];
                d = Math.min(d, plusProcheSurLigne(p, a, b, 'segment').d);
            }
            return Number.isFinite(d) ? d : null;
        }
        default:
            /* Une transformation, une marque, un texte : rien où poser un point. */
            return null;
    }
}
/** Le domaine effectivement TRACÉ d'une courbe : le sien, coupé à la fenêtre. */
function domaineTrace(domaine, r) {
    return [Math.max(domaine?.[0] ?? r.xMin, r.xMin), Math.min(domaine?.[1] ?? r.xMax, r.xMax)];
}
/**
 * L'objet de l'élève pris comme une COURBE, avec les bornes qu'il trace
 * (§ 5.1, `courbe_egale`) : une fonction donne la sienne ; une droite, une
 * demi-droite ou un segment se lisent comme la fonction affine qu'ils
 * portent. Une VERTICALE n'est le graphique d'aucune fonction : `null`.
 */
function courbeDe(o, r) {
    if (o.genre === 'fonction')
        return { f: o.f, bornes: o.domaine };
    if (o.genre !== 'ligne')
        return null;
    const d = moins(o.b, o.a);
    if (Math.abs(d.x) < EPSILON)
        return null;
    const pente = d.y / d.x;
    const f = { famille: 'lineaire', a: pente, b: o.a.y - pente * o.a.x };
    /* Ce que le tracé BORNE vraiment : un segment par ses deux bouts, une
       demi-droite par son origine seule, une droite par rien. */
    if (o.sorte === 'segment') {
        return { f, bornes: [Math.min(o.a.x, o.b.x), Math.max(o.a.x, o.b.x)] };
    }
    if (o.sorte === 'demi_droite') {
        const versLaDroite = d.x > 0;
        return { f, bornes: versLaDroite ? [o.a.x, r.xMax] : [r.xMin, o.a.x] };
    }
    return { f, bornes: null };
}
/**
 * De quel côté d'une frontière (a, b) tombe un point, le côté demandé comptant
 * pour positif. Zéro : sur la frontière.
 *
 * Le côté se dit en unités de FIGURE et non dans le sens du tracé : « dessus »
 * veut toujours dire y croissant, quel que soit le sens dans lequel l'élève a
 * tiré sa droite. Deux élèves qui tracent la même frontière en sens contraire
 * doivent hachurer le même demi-plan.
 */
/**
 * L'INVERSE de `cotePourPoint` : quel mot désigne le demi-plan qui CONTIENT
 * ce point ?
 *
 * Décision du fondateur (12 septembre 2026) : l'élève ne choisit pas un mot,
 * il touche un côté. Le mot n'est plus qu'une écriture interne — et il faut
 * bien en écrire un, puisque c'est ce que porte le document.
 *
 * On essaie « dessus » : si le point y tombe du bon côté, c'est le mot. Sinon
 * « dessous ». Sur une frontière VERTICALE, ces deux-là ne disent rien (la
 * normale n'a pas de composante verticale utile) : on bascule alors sur
 * « droite » / « gauche ». C'est exactement la dégénérescence que le
 * vérificateur traite en jugeant par points témoins plutôt que par le mot.
 */
export function coteDuPoint(p, a, b) {
    const verticale = Math.abs(b.x - a.x) < EPSILON;
    if (verticale)
        return cotePourPoint(p, a, b, 'droite') >= 0 ? 'droite' : 'gauche';
    return cotePourPoint(p, a, b, 'dessus') >= 0 ? 'dessus' : 'dessous';
}
function cotePourPoint(p, a, b, cote) {
    let nx = -(b.y - a.y);
    let ny = b.x - a.x;
    const vers = cote === 'dessus' ? ny : cote === 'dessous' ? -ny : cote === 'droite' ? nx : -nx;
    if (vers < 0) {
        nx = -nx;
        ny = -ny;
    }
    const n = Math.hypot(nx, ny);
    return n < EPSILON ? 0 : ((p.x - a.x) * nx + (p.y - a.y) * ny) / n;
}
/** Les longueurs des côtés d'un polygone, dans l'ordre. */
function cotesDe(s) {
    return s.map((p, i) => distance(p, s[(i + 1) % s.length]));
}
/** Les angles intérieurs d'un polygone, dans l'ordre des sommets. */
function anglesDe(s) {
    const n = s.length;
    return s.map((p, i) => mesureAngle(p, s[(i + n - 1) % n], s[(i + 1) % n]));
}
/**
 * Le plus petit écart maximal entre deux listes de points, sur toutes les
 * permutations CIRCULAIRES et dans les deux sens : un élève peut nommer son
 * triangle en partant de n'importe quel sommet, et dans n'importe quel sens
 * (§ 5.1, `image`). `null` : les listes n'ont pas la même longueur.
 */
function ecartCirculaire(attendus, obtenus) {
    const n = attendus.length;
    if (n === 0 || obtenus.length !== n)
        return null;
    let meilleur = Infinity;
    for (const sens of [1, -1]) {
        for (let depart = 0; depart < n; depart += 1) {
            let pire = 0;
            for (let i = 0; i < n; i += 1) {
                const j = (((depart + sens * i) % n) + n) % n;
                pire = Math.max(pire, distance(attendus[i], obtenus[j]));
            }
            meilleur = Math.min(meilleur, pire);
        }
    }
    return Number.isFinite(meilleur) ? meilleur : null;
}
/** L'objet désigné par une référence : une cible liée, ou un objet de l'énoncé. */
function objetRef(r, ctx, liaison) {
    if (typeof r !== 'object' || r === null)
        return null;
    const o = r;
    if (typeof o.cible === 'string')
        return liaison.get(o.cible) ?? null;
    if (typeof o.enonce === 'string') {
        /* Les contraintes citent un point de l'énoncé par son NOM (« A ») ; les
         * autres objets par leur indice (« e:3 »). Voir § 1.1, principe 4. */
        const id = estIdEnonce(o.enonce) ? o.enonce : `e:${o.enonce}`;
        const x = ctx.resolue.objets.get(id);
        return x !== undefined && x.genre !== 'indefini' ? x : null;
    }
    return null;
}
/** Le point désigné par une référence : une valeur littérale, ou un objet ponctuel. */
function pointRef(r, ctx, liaison) {
    if (typeof r === 'object' && r !== null) {
        const o = r;
        const x = nombreDe(o.x);
        const y = nombreDe(o.y);
        if (x !== null && y !== null)
            return { x, y };
    }
    const obj = objetRef(r, ctx, liaison);
    return obj !== null && obj.genre === 'point' ? obj.p : null;
}
/** La ligne désignée par une référence. */
function ligneRef(r, ctx, liaison) {
    const o = objetRef(r, ctx, liaison);
    return o !== null && o.genre === 'ligne' ? { a: o.a, b: o.b, sorte: o.sorte } : null;
}
/** La transformation décrite par l'attendu — calculée par le CODE, jamais reçue. */
function transformationRef(t, ctx, liaison) {
    if (typeof t !== 'object' || t === null)
        return null;
    const o = t;
    switch (o.type) {
        case 'translation': {
            /* Soit deux points (de → a), soit les composantes du vecteur. */
            const de = pointRef(o.de, ctx, liaison);
            const a = pointRef(o.a, ctx, liaison);
            const u = de !== null && a !== null
                ? moins(a, de)
                : (() => {
                    const x = nombreDe(o.x);
                    const y = nombreDe(o.y);
                    return x !== null && y !== null ? { x, y } : null;
                })();
            if (u === null || !tousFinis(u.x, u.y))
                return null;
            return (p) => plus(p, u);
        }
        case 'rotation': {
            const c = pointRef(o.centre, ctx, liaison);
            const angle = nombreDe(o.angle);
            if (c === null || angle === null)
                return null;
            return (p) => tourner(p, c, angle);
        }
        case 'reflexion': {
            const l = ligneRef(o.axe, ctx, liaison);
            if (l === null || unitaire(directionDe(l)) === null)
                return null;
            return (p) => refleter(p, l.a, l.b) ?? { x: Number.NaN, y: Number.NaN };
        }
        case 'homothetie': {
            const c = pointRef(o.centre, ctx, liaison);
            const k = nombreDe(o.rapport);
            if (c === null || k === null || k === 0)
                return null;
            return (p) => homothetie(p, c, k);
        }
        default:
            return null;
    }
}
/* ================================================================ le catalogue */
const estObjetBrut = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** Le côté d'un demi-plan écrit dans l'attendu ; `null` : absent ou inconnu. */
function coteDemiPlan(v) {
    return v === 'dessus' || v === 'dessous' || v === 'gauche' || v === 'droite' ? v : null;
}
/**
 * La fonction que l'attendu décrit (`famille`, `parametres`, `domaine`) —
 * lue par la MÊME conversion que la figure d'un défi, pour que la courbe
 * attendue et la courbe dessinée ne puissent pas différer.
 */
function fonctionAttendue(c) {
    const famille = familleFonctionV2(c.famille);
    if (famille === null)
        return null;
    const r = fonctionCanoniqueV2(famille, c.parametres, c.domaine);
    return 'griefs' in r ? null : r;
}
/**
 * La période d'une courbe PÉRIODIQUE (sinusoïde : 2π/|b| ; tangente : π/|b|) ;
 * `null` pour les autres familles.
 */
function periodeDe(f) {
    if (f.famille === 'sinusoidale')
        return (2 * Math.PI) / Math.abs(f.b);
    if (f.famille === 'tangente')
        return Math.PI / Math.abs(f.b);
    return null;
}
/**
 * Le nombre d'intervalles où deux courbes se comparent sur [x0 ; x1] : 40 —
 * assez pour qu'une parabole ne se glisse pas entre deux points de contrôle —,
 * et, pour une courbe PÉRIODIQUE (tranche T44), au moins 24 par période de la
 * plus rapide des deux : une vague trop serrée ne passe pas entre les mailles.
 */
function intervallesDeComparaison(a, b, x0, x1) {
    const periodes = [periodeDe(a), periodeDe(b)].filter((p) => p !== null && p > 0);
    if (periodes.length === 0)
        return 40;
    return Math.min(2000, Math.max(40, Math.ceil((24 * (x1 - x0)) / Math.min(...periodes))));
}
/**
 * Le plus grand écart VERTICAL entre deux courbes sur [x0 ; x1], à la règle de
 * `courbe_egale` — et de chaque partie de `parties_egales` :
 *
 *  - UN LOGARITHME PLONGE le long de son asymptote, une fonction RATIONNELLE
 *    aussi (des deux côtés de la sienne), une TANGENTE à chacune des siennes :
 *    là où la courbe attendue sort de la fenêtre, on ne la voit pas, et un
 *    écart vertical n'y veut rien dire (il tend vers l'infini pour un décalage
 *    infime). Elle se compare là où elle se VOIT ;
 *  - la TANGENTE (tranche T44) exclut en outre un voisinage de chacune de ses
 *    asymptotes (un vingtième de période de part et d'autre), même dans une
 *    fenêtre haute : la même courbe y prend des valeurs immenses, et l'écart
 *    entre deux écritures se perdrait dans l'arrondi ;
 *  - une SINUSOÏDE se compare partout, sans exclusion : 2 sin(x − π/2) + 1,
 *    −2 cos x + 1 et 2 sin(x + 3π/2) + 1 donnent les MÊMES valeurs, donc la
 *    même courbe, quelle que soit l'écriture.
 *
 * `absente` : la courbe tracée n'existe pas en une abscisse où l'attendue passe.
 */
function ecartDeCourbes(attendue, tracee, x0, x1, r) {
    const n = intervallesDeComparaison(attendue, tracee, x0, x1);
    const plonge = attendue.famille === 'logarithmique' ||
        attendue.famille === 'rationnelle' ||
        attendue.famille === 'tangente';
    /* Les asymptotes de la tangente attendue, et le voisinage exclu autour de chacune. */
    const murs = attendue.famille === 'tangente' ? asymptotesTangenteV2(attendue, x0 - 1, x1 + 1) : [];
    const voisinage = attendue.famille === 'tangente' ? Math.PI / Math.abs(attendue.b) / 20 : 0;
    let pire = 0;
    let ou = null;
    for (let i = 0; i <= n; i += 1) {
        const x = x0 + ((x1 - x0) * i) / n;
        if (murs.some((m) => Math.abs(x - m) < voisinage))
            continue;
        const ya = evaluerFonctionV2(attendue, x);
        const yb = evaluerFonctionV2(tracee, x);
        if (!Number.isFinite(ya))
            continue;
        if (plonge && (ya < r.yMin || ya > r.yMax))
            continue;
        if (!Number.isFinite(yb))
            return { absente: { x, y: ya } };
        const e = Math.abs(ya - yb);
        if (e > pire) {
            pire = e;
            ou = { x, y: ya };
        }
    }
    return { pire, ou };
}
const MOT_BOUT = {
    plein: 'un point plein',
    vide: 'un point vide',
    aucun: 'sans point',
};
/**
 * `parties_egales` (tranche T42) : la fonction définie par parties tracée par
 * l'élève, PARTIE PAR PARTIE. Ses parties sont TOUTES ses courbes (outil
 * « fonction »), chacune bornée par son domaine et marquée à ses bouts. Chaque
 * partie attendue doit être suivie par une partie tracée — même courbe à la
 * tolérance de `courbe_egale`, même intervalle à la tolérance près —, avec les
 * mêmes bouts pleins ou vides (un bout attendu « aucun » accepte tout). Une
 * partie tracée de trop, deux bouts pleins au même x de valeurs différentes :
 * non tenue.
 */
function mesurerParties(c, ctx, liee) {
    const lues = lirePartiesV2(c.parties);
    if (liee === null || 'griefs' in lues)
        return indeterminee('Aucune courbe tracée.');
    const r = ctx.resolue.repere;
    const tol = ctx.tol.distance;
    const tracees = [];
    for (const id of ctx.defs.keys()) {
        const o = ctx.resolue.objets.get(id);
        if (o === undefined || o.genre !== 'fonction')
            continue;
        tracees.push({
            f: o.f,
            domaine: o.domaine ?? [r.xMin, r.xMax],
            bouts: o.bouts ?? ['aucun', 'aucun'],
        });
    }
    /* Deux bouts pleins au même x, de valeurs différentes : ce n'est plus une fonction. */
    for (let i = 0; i < tracees.length; i += 1) {
        for (let j = i + 1; j < tracees.length; j += 1) {
            const p = tracees[i];
            const q = tracees[j];
            if (p === undefined || q === undefined)
                continue;
            const conflit = boutsPleinsEnConflit({ fonction: p.f, domaine: p.domaine, bouts: p.bouts }, { fonction: q.f, domaine: q.domaine, bouts: q.bouts }, (u, v) => Math.abs(u - v) <= tol);
            if (conflit !== null) {
                return tranche(false, null, null, null, { x: conflit.x, y: conflit.y1 }, `Deux bouts pleins en x = ${nb(conflit.x)}, de valeurs ${nb(conflit.y1)} et ${nb(conflit.y2)} : ce n'est plus une fonction.`);
            }
        }
    }
    const prises = new Set();
    let pire = 0;
    for (const attendue of lues.parties) {
        const [a0, a1] = attendue.domaine;
        const x0 = Math.max(a0, r.xMin);
        const x1 = Math.min(a1, r.xMax);
        if (!(x1 > x0))
            continue;
        /* La partie tracée la plus proche : même intervalle, puis même courbe. */
        let meilleure = null;
        tracees.forEach((t, k) => {
            if (prises.has(k))
                return;
            const eBornes = Math.max(Math.abs(t.domaine[0] - a0), Math.abs(t.domaine[1] - a1));
            if (eBornes > tol)
                return;
            const e = ecartDeCourbes(attendue.fonction, t.f, x0, x1, r);
            if ('absente' in e)
                return;
            const total = Math.max(e.pire, eBornes);
            if (total <= tol && (meilleure === null || total < meilleure.e))
                meilleure = { k, e: total };
        });
        const trouvee = meilleure;
        if (trouvee === null) {
            const xm = (x0 + x1) / 2;
            return tranche(false, null, null, null, { x: xm, y: evaluerFonctionV2(attendue.fonction, xm) }, `Aucune partie tracée ne suit celle attendue de x = ${nb(a0)} à x = ${nb(a1)}.`);
        }
        prises.add(trouvee.k);
        pire = Math.max(pire, trouvee.e);
        const t = tracees[trouvee.k];
        for (const i of [0, 1]) {
            const vise = attendue.bouts[i];
            const porte = t?.bouts[i] ?? 'aucun';
            if (vise === 'aucun' || vise === porte)
                continue;
            const x = attendue.domaine[i];
            return tranche(false, null, null, null, { x, y: evaluerFonctionV2(attendue.fonction, x) }, `En x = ${nb(x)}, la partie finit ${MOT_BOUT[porte]} ; ${MOT_BOUT[vise]} attendu.`);
        }
    }
    const deTrop = tracees.findIndex((_t, k) => !prises.has(k));
    if (deTrop >= 0) {
        const t = tracees[deTrop];
        const xm = t === undefined ? 0 : (t.domaine[0] + t.domaine[1]) / 2;
        return tranche(false, null, null, null, t === undefined ? null : { x: xm, y: evaluerFonctionV2(t.f, xm) }, t === undefined
            ? 'Une partie de trop.'
            : `Une partie de trop, de x = ${nb(t.domaine[0])} à x = ${nb(t.domaine[1])}.`);
    }
    return tranche(true, pire, 0, pire, null, `Les ${lues.parties.length} parties suivent celles attendues à ${nb(pire)} près ; leurs bouts sont justes.`);
}
const indeterminee = (texte) => ({ etat: 'indeterminee', texte });
/** Un constat tranché par une valeur mesurée et un écart comparé à la tolérance. */
function tranche(ok, mesure, attendu, ecart, ou, texte) {
    return { etat: ok ? 'tenue' : 'non_tenue', mesure, attendu, ecart, ou, texte };
}
/**
 * Le catalogue du § 5.1 : ce que chaque contrainte mesure, et à quoi elle le
 * compare. Une seule règle traverse tout le tableau — la mesure est faite par
 * le code sur la figure RÉSOLUE de l'élève, jamais lue dans un texte.
 */
/** Les caractéristiques qu'une contrainte `caracteristiques` déclare (L6, F8) — lues sans rien supposer. */
function caracteristiquesAttendues(c) {
    const point = (v) => estObjetBrut(v) && nombreDe(v.x) !== null && nombreDe(v.y) !== null
        ? { x: v.x, y: v.y }
        : undefined;
    const sommet = point(c.sommet);
    const zeros = Array.isArray(c.zeros)
        ? c.zeros.filter((z) => nombreDe(z) !== null)
        : undefined;
    const sens = Array.isArray(c.sens)
        ? c.sens.filter((x) => x === 'croissante' || x === 'decroissante')
        : undefined;
    const as = estObjetBrut(c.asymptotes) ? c.asymptotes : null;
    const h = as === null ? null : nombreDe(as.horizontale);
    const v = as === null ? null : nombreDe(as.verticale);
    return {
        ...(sommet !== undefined ? { sommet } : {}),
        ...(c.ouverture === 'haut' || c.ouverture === 'bas' ? { ouverture: c.ouverture } : {}),
        ...(zeros !== undefined ? { zeros } : {}),
        ...(sens !== undefined ? { sens } : {}),
        ...(h !== null || v !== null
            ? {
                asymptotes: {
                    ...(h !== null ? { horizontale: h } : {}),
                    ...(v !== null ? { verticale: v } : {}),
                },
            }
            : {}),
    };
}
function mesurer(c, ctx, liaison) {
    const tol = ctx.tol;
    const pt = (r) => pointRef(r, ctx, liaison);
    const obj = (r) => objetRef(r, ctx, liaison);
    const lig = (r) => ligneRef(r, ctx, liaison);
    switch (c.type) {
        /* ─────────────────────────── les points ─────────────────────────── */
        case 'point_en': {
            const p = pt(c.point);
            const x = nombreDe(c.x);
            const y = nombreDe(c.y);
            if (p === null || x === null || y === null)
                return indeterminee('Aucun point à cet endroit.');
            const d = distance(p, { x, y });
            return tranche(d <= tol.distance, d, 0, d, p, `Le point est à ${nb(d)} de (${nb(x)} ; ${nb(y)}) ; ${nb(tol.distance)} au plus.`);
        }
        case 'milieu': {
            const p = pt(c.point);
            const a = pt(c.de);
            const b = pt(c.a);
            if (p === null || a === null || b === null)
                return indeterminee('Aucun point placé.');
            const m = milieu(a, b);
            const d = distance(p, m);
            return tranche(d <= tol.distance, d, 0, d, p, `Le point est à ${nb(d)} du milieu (${nb(m.x)} ; ${nb(m.y)}) ; ${nb(tol.distance)} au plus.`);
        }
        case 'sur': {
            const p = pt(c.point);
            const o = obj(c.objet);
            if (p === null || o === null)
                return indeterminee('Aucun point, ou aucun objet à rejoindre.');
            const d = distanceAObjet(p, o, ctx.resolue.repere);
            if (d === null)
                return indeterminee("Cet objet n'a pas de tracé où poser un point.");
            return tranche(d <= tol.distance, d, 0, d, p, `Le point est à ${nb(d)} de l'objet ; ${nb(tol.distance)} au plus.`);
        }
        case 'intersection': {
            const p = pt(c.point);
            const refs = Array.isArray(c.objets) ? c.objets : [];
            if (p === null || refs.length < 2)
                return indeterminee('Aucun point à une intersection.');
            let pire = 0;
            for (const r of refs) {
                const o = obj(r);
                const d = o === null ? null : distanceAObjet(p, o, ctx.resolue.repere);
                if (d === null)
                    return indeterminee("L'un des deux objets manque.");
                pire = Math.max(pire, d);
            }
            return tranche(pire <= tol.distance, pire, 0, pire, p, `Le point est à ${nb(pire)} du plus éloigné des deux objets ; ${nb(tol.distance)} au plus.`);
        }
        case 'equidistant': {
            const p = pt(c.point);
            const a = pt(c.de);
            const b = pt(c.a);
            if (p === null || a === null || b === null)
                return indeterminee('Aucun point placé.');
            const pa = distance(p, a);
            const pb = distance(p, b);
            const grand = Math.max(pa, pb);
            if (grand < EPSILON)
                return indeterminee('Les trois points sont confondus.');
            const e = Math.abs(pa - pb) / grand;
            return tranche(e <= tol.relative, pa, pb, e, p, `Le point est à ${nb(pa)} de l'un et ${nb(pb)} de l'autre ; écart de ${pourcent(e)}.`);
        }
        /* ─────────────────────────── les lignes ─────────────────────────── */
        case 'passe_par': {
            const o = obj(c.ligne);
            const p = pt(c.point);
            if (o === null || p === null)
                return indeterminee('Aucune ligne tracée.');
            const d = distanceAObjet(p, o, ctx.resolue.repere);
            if (d === null)
                return indeterminee("Cet objet n'a pas de tracé.");
            return tranche(d <= tol.distance, d, 0, d, p, `La ligne passe à ${nb(d)} du point ; ${nb(tol.distance)} au plus.`);
        }
        case 'perpendiculaire':
        case 'parallele': {
            const l1 = lig(c.ligne);
            const l2 = lig(c.a);
            if (l1 === null || l2 === null)
                return indeterminee('Aucune ligne tracée.');
            const angle = angleLignes(l1, l2);
            if (angle === null)
                return indeterminee('Une des lignes est dégénérée.');
            const vise = c.type === 'perpendiculaire' ? 90 : 0;
            const e = Math.abs(angle - vise);
            return tranche(e <= tol.angleDeg, angle, vise, e, milieu(l1.a, l1.b), `La ligne fait ${nb(angle)}° avec l'autre ; ${nb(vise)}° attendus.`);
        }
        case 'mediatrice': {
            const l = lig(c.ligne);
            const a = pt(c.de);
            const b = pt(c.a);
            if (l === null || a === null || b === null)
                return indeterminee('Aucune ligne tracée.');
            const angle = angleDirections(directionDe(l), moins(b, a));
            if (angle === null)
                return indeterminee('Une des lignes est dégénérée.');
            const m = milieu(a, b);
            /* Deux conditions, et on rapporte celle qui manque : la médiatrice est
             * perpendiculaire au segment ET passe par son milieu (§ 5.1). */
            const dMilieu = plusProcheSurLigne(m, l.a, l.b, l.sorte).d;
            const eAngle = Math.abs(angle - 90);
            const angleOk = eAngle <= tol.angleDeg;
            const milieuOk = dMilieu <= tol.distance;
            if (!angleOk) {
                return tranche(false, angle, 90, eAngle, m, `La ligne fait ${nb(angle)}° avec le segment ; 90° attendus.`);
            }
            return tranche(milieuOk, dMilieu, 0, dMilieu, m, milieuOk
                ? `La ligne est perpendiculaire au segment et passe à ${nb(dMilieu)} de son milieu.`
                : `La ligne passe à ${nb(dMilieu)} du milieu du segment ; ${nb(tol.distance)} au plus.`);
        }
        case 'bissectrice': {
            const l = lig(c.ligne);
            const s = pt(c.sommet);
            const a = pt(c.de);
            const b = pt(c.a);
            if (l === null || s === null || a === null || b === null) {
                return indeterminee('Aucune ligne tracée.');
            }
            const u = unitaire(moins(a, s));
            const v = unitaire(moins(b, s));
            if (u === null || v === null)
                return indeterminee('Un côté part du sommet sans longueur.');
            const dSommet = plusProcheSurLigne(s, l.a, l.b, l.sorte).d;
            if (dSommet > tol.distance) {
                return tranche(false, dSommet, 0, dSommet, s, `La ligne passe à ${nb(dSommet)} du sommet ; ${nb(tol.distance)} au plus.`);
            }
            /* La somme de deux vecteurs unitaires pointe DANS l'angle : c'est la
             * bissectrice intérieure. La bissectrice EXTÉRIEURE fait pourtant des
             * angles égaux avec les deux côtés — l'égalité des angles ne suffit
             * donc pas, il faut la direction (§ 5.1). */
            const interieure = unitaire(plus(u, v));
            if (interieure === null)
                return indeterminee('Les deux côtés sont opposés.');
            const d = directionDe(l);
            const aSA = angleDirections(d, moins(a, s));
            const aSB = angleDirections(d, moins(b, s));
            if (aSA === null || aSB === null)
                return indeterminee('La ligne est dégénérée.');
            const dedans = angleDirections(d, interieure);
            const ecartAngles = Math.abs(aSA - aSB);
            const ok = ecartAngles <= tol.angleDeg && dedans !== null && dedans <= tol.angleDeg;
            return tranche(ok, ecartAngles, 0, ecartAngles, s, dedans !== null && dedans > tol.angleDeg
                ? `La ligne ne partage pas l'angle par l'intérieur : elle en est écartée de ${nb(dedans)}°.`
                : `La ligne fait ${nb(aSA)}° d'un côté et ${nb(aSB)}° de l'autre ; écart de ${nb(ecartAngles)}°.`);
        }
        case 'droite_par': {
            const o = obj(c.ligne);
            const p1 = pt(c.p1);
            const p2 = pt(c.p2);
            if (o === null || o.genre !== 'ligne' || p1 === null || p2 === null) {
                return indeterminee('Aucune ligne tracée.');
            }
            const l = { a: o.a, b: o.b, sorte: o.sorte };
            if (l.sorte === 'segment') {
                /* Un SEGMENT par deux points : ce sont ses extrémités, dans un sens
                 * ou dans l'autre — pas seulement sa droite porteuse. */
                const direct = Math.max(distance(l.a, p1), distance(l.b, p2));
                const inverse = Math.max(distance(l.a, p2), distance(l.b, p1));
                const d = Math.min(direct, inverse);
                return tranche(d <= tol.distance, d, 0, d, milieu(p1, p2), `Les extrémités du segment sont à ${nb(d)} des points demandés ; ${nb(tol.distance)} au plus.`);
            }
            const d = Math.max(plusProcheSurLigne(p1, l.a, l.b, l.sorte).d, plusProcheSurLigne(p2, l.a, l.b, l.sorte).d);
            return tranche(d <= tol.distance, d, 0, d, milieu(p1, p2), `La ligne passe à ${nb(d)} du plus éloigné des deux points ; ${nb(tol.distance)} au plus.`);
        }
        case 'pente': {
            const l = lig(c.ligne);
            const v = nombreDe(c.valeur);
            if (l === null || v === null)
                return indeterminee('Aucune ligne tracée.');
            if (unitaire(directionDe(l)) === null)
                return indeterminee('La ligne est dégénérée.');
            /* On compare des ANGLES, pas des pentes : deux pentes de 100 et 120 sont
             * presque la même droite, alors que leur écart relatif est énorme. */
            const angle = angleDePente(l);
            const vise = (Math.atan(v) * 180) / Math.PI;
            const e = Math.abs(angle - vise);
            return tranche(e <= tol.angleDeg, angle, vise, e, milieu(l.a, l.b), `La ligne penche de ${nb(angle)}° ; ${nb(vise)}° attendus (pente ${nb(v)}).`);
        }
        case 'ordonnee_origine': {
            const l = lig(c.ligne);
            const v = nombreDe(c.valeur);
            if (l === null || v === null)
                return indeterminee('Aucune ligne tracée.');
            const d = directionDe(l);
            if (Math.abs(d.x) < EPSILON)
                return indeterminee("La ligne est verticale : pas d'ordonnée.");
            const b = l.a.y + ((0 - l.a.x) * d.y) / d.x;
            const e = Math.abs(b - v);
            return tranche(e <= tol.distance, b, v, e, { x: 0, y: b }, `La ligne coupe l'axe des y en ${nb(b)} ; ${nb(v)} attendu.`);
        }
        /* ─────────────────────────── les grandeurs ──────────────────────── */
        case 'longueur': {
            const v = nombreDe(c.valeur);
            const de = pt(c.de);
            const a = pt(c.a);
            const seg = de === null || a === null ? lig(c.segment ?? c.ligne) : null;
            const p1 = de ?? seg?.a ?? null;
            const p2 = a ?? seg?.b ?? null;
            if (v === null || p1 === null || p2 === null)
                return indeterminee('Aucune longueur à mesurer.');
            const L = distance(p1, p2);
            if (Math.abs(v) < EPSILON)
                return indeterminee('La longueur attendue est nulle.');
            const e = Math.abs(L - v) / Math.abs(v);
            return tranche(e <= tol.relative, L, v, e, milieu(p1, p2), `La longueur mesure ${nb(L)} ; ${nb(v)} attendu (écart de ${pourcent(e)}).`);
        }
        case 'angle': {
            const s = pt(c.sommet);
            const a = pt(c.de);
            const b = pt(c.a);
            const v = nombreDe(c.valeur);
            if (s === null || a === null || b === null || v === null) {
                return indeterminee('Aucun angle à mesurer.');
            }
            if (distance(s, a) < EPSILON || distance(s, b) < EPSILON) {
                return indeterminee('Un côté part du sommet sans longueur.');
            }
            const m = mesureAngle(s, a, b);
            const e = Math.abs(m - v);
            return tranche(e <= tol.angleDeg, m, v, e, s, `L'angle mesure ${nb(m)}° ; ${nb(v)}° attendus.`);
        }
        case 'rayon': {
            const o = obj(c.cercle);
            const v = nombreDe(c.valeur);
            if (o === null || o.genre !== 'cercle' || v === null)
                return indeterminee('Aucun cercle tracé.');
            if (Math.abs(v) < EPSILON)
                return indeterminee('Le rayon attendu est nul.');
            const e = Math.abs(o.rayon - v) / Math.abs(v);
            return tranche(e <= tol.relative, o.rayon, v, e, o.centre, `Le rayon mesure ${nb(o.rayon)} ; ${nb(v)} attendu (écart de ${pourcent(e)}).`);
        }
        case 'aire': {
            const o = obj(c.polygone);
            const v = nombreDe(c.valeur);
            if (o === null || o.genre !== 'polygone' || v === null) {
                return indeterminee('Aucun polygone tracé.');
            }
            if (Math.abs(v) < EPSILON)
                return indeterminee("L'aire attendue est nulle.");
            /* L'aire SIGNÉE dépend du sens de parcours ; l'aire, non. */
            const aire = Math.abs(aireSignee(o.sommets));
            const e = Math.abs(aire - v) / Math.abs(v);
            return tranche(e <= tol.relative, aire, v, e, milieu(o.sommets[0], o.sommets[1]), `L'aire mesure ${nb(aire)} ; ${nb(v)} attendue (écart de ${pourcent(e)}).`);
        }
        /* ────────────────────── les transformations ─────────────────────── */
        case 'image':
            return mesurerImage(c, ctx, liaison);
        case 'isometrique':
        case 'semblable':
            return mesurerSemblable(c, ctx, liaison);
        /* ───────────────────────── l'écriture ───────────────────────────── */
        case 'nomme': {
            const o = typeof c.cible === 'string' ? (liaison.get(c.cible) ?? null) : obj(c.cible);
            if (o === null)
                return indeterminee('Aucun objet trouvé.');
            const noms = Array.isArray(c.noms) ? c.noms : null;
            if (noms !== null) {
                /* Un polygone : ses SOMMETS portent les noms, dans un ordre circulaire. */
                const attendus = noms.filter((n) => typeof n === 'string').map(normaliserNom);
                const obtenus = nomsDesSommets(o, ctx);
                if (obtenus === null)
                    return indeterminee('Cet objet n’a pas de sommets nommés.');
                const ok = memeCycle(attendus, obtenus);
                return {
                    etat: ok ? 'tenue' : 'non_tenue',
                    mesure: null,
                    attendu: null,
                    ecart: null,
                    ou: null,
                    texte: ok
                        ? `Les sommets portent les noms ${attendus.join(', ')}.`
                        : `Les sommets se nomment ${obtenus.length === 0 ? '(sans nom)' : obtenus.join(', ')} ; ${attendus.join(', ')} attendus.`,
                };
            }
            const attendu = typeof c.nom === 'string' ? normaliserNom(c.nom) : null;
            if (attendu === null)
                return indeterminee('Aucun nom demandé.');
            const porte = o.nom === undefined ? '' : normaliserNom(o.nom);
            const ok = porte === attendu;
            return {
                etat: ok ? 'tenue' : 'non_tenue',
                mesure: null,
                attendu: null,
                ecart: null,
                ou: null,
                texte: ok
                    ? `L'objet est nommé ${attendu}.`
                    : `L'objet est nommé ${porte === '' ? '(sans nom)' : porte} ; ${attendu} attendu.`,
            };
        }
        case 'style': {
            const o = typeof c.cible === 'string' ? (liaison.get(c.cible) ?? null) : obj(c.cible);
            if (o === null)
                return indeterminee('Aucun objet trouvé.');
            const attendu = c.style === 'pointille' ? 'pointille' : 'plein';
            const porte = o.style ?? 'plein';
            const ok = porte === attendu;
            return {
                etat: ok ? 'tenue' : 'non_tenue',
                mesure: null,
                attendu: null,
                ecart: null,
                ou: null,
                texte: ok
                    ? `Le tracé est en ${attendu === 'pointille' ? 'pointillé' : 'trait plein'}.`
                    : `Le tracé est en ${porte === 'pointille' ? 'pointillé' : 'trait plein'} ; ${attendu === 'pointille' ? 'pointillé' : 'trait plein'} attendu.`,
            };
        }
        /* ─────────────── le repère et les fonctions (phase 3) ───────────────── */
        case 'courbe_egale': {
            const o = obj(c.objet);
            const attendue = fonctionAttendue(c);
            if (o === null || attendue === null)
                return indeterminee('Aucune courbe tracée.');
            const tracee = courbeDe(o, ctx.resolue.repere);
            if (tracee === null) {
                return indeterminee("Le tracé n'est le graphique d'aucune fonction.");
            }
            const [x0, x1] = domaineTrace(attendue.domaine, ctx.resolue.repere);
            if (!(x1 > x0))
                return indeterminee('La courbe attendue ne passe pas dans la fenêtre.');
            /* La comparaison même de chaque partie d'une fonction par parties. */
            const e = ecartDeCourbes(attendue.fonction, tracee.f, x0, x1, ctx.resolue.repere);
            if ('absente' in e) {
                return tranche(false, null, null, null, e.absente, `La courbe tracée n'existe pas en x = ${nb(e.absente.x)}, où la courbe attendue passe.`);
            }
            const { pire, ou } = e;
            if (pire > tol.distance) {
                return tranche(false, pire, 0, pire, ou, `La courbe s'écarte de ${nb(pire)} de celle attendue ; ${nb(tol.distance)} au plus.`);
            }
            /* Le DOMAINE : « trace sur [0 ; 6] » demande un tracé borné. On ne le
               regarde que si l'attendu en donne un — sinon toute la fenêtre va. */
            if (attendue.domaine !== null) {
                const b = tracee.bornes;
                if (b === null) {
                    return tranche(false, null, null, null, ou, `Le tracé ne s'arrête pas : [${nb(attendue.domaine[0])} ; ${nb(attendue.domaine[1])}] attendu.`);
                }
                const eBornes = Math.max(Math.abs(b[0] - attendue.domaine[0]), Math.abs(b[1] - attendue.domaine[1]));
                if (eBornes > tol.distance) {
                    return tranche(false, eBornes, 0, eBornes, { x: b[0], y: evaluerFonctionV2(tracee.f, b[0]) }, `Le tracé va de ${nb(b[0])} à ${nb(b[1])} ; de ${nb(attendue.domaine[0])} à ${nb(attendue.domaine[1])} attendu.`);
                }
            }
            return tranche(true, pire, 0, pire, ou, `La courbe suit celle attendue à ${nb(pire)} près ; ${nb(tol.distance)} au plus.`);
        }
        case 'parties_egales':
            return mesurerParties(c, ctx, obj(c.objet));
        case 'caracteristiques': {
            /* L6 (F8) : la courbe — esquissée, ou la fonction de la référence échantillonnée
               dans la fenêtre — se RÉDUIT par la même fonction ; la lecture est comparée à
               ce que la règle donne, à une demi-case près. */
            const o = obj(c.objet);
            if (o === null)
                return indeterminee('Aucune courbe esquissée.');
            const r = ctx.resolue.repere;
            const traits = o.genre === 'marque' && o.sorte === 'esquisse'
                ? o.traits
                : o.genre === 'fonction'
                    ? traitsDeLaFonction((x) => evaluerFonctionV2(o.f, x), r, o.domaine)
                    : null;
            if (traits === null)
                return indeterminee("Le tracé n'est pas une courbe.");
            const lue = reduireEsquisse(traits, r);
            if (!lue.lue)
                return indeterminee(lue.raison);
            const j = jugerCaracteristiques(lue.caracteristiques, caracteristiquesAttendues(c), r);
            const dite = j.points.find((x) => !x.tenue) ?? j.points[0];
            const sommet = lue.caracteristiques.extremums[0];
            return tranche(j.tenue, null, null, null, sommet !== undefined ? { x: sommet.x, y: sommet.y } : null, dite?.texte ?? 'Rien à comparer : la contrainte ne déclare aucune caractéristique.');
        }
        case 'demi_plan': {
            const o = obj(c.objet);
            if (o === null || o.genre !== 'demi_plan')
                return indeterminee('Aucun demi-plan hachuré.');
            const attendue = fonctionAttendue(estObjetBrut(c.frontiere) ? c.frontiere : c);
            const coteVise = coteDemiPlan(c.cote);
            if (attendue === null || attendue.fonction.famille !== 'lineaire' || coteVise === null) {
                return indeterminee("L'attendu ne décrit pas un demi-plan.");
            }
            /* La frontière attendue, prise par deux de ses points. */
            const fa = { x: 0, y: attendue.fonction.b };
            const fb = { x: 1, y: attendue.fonction.a + attendue.fonction.b };
            /* 1. la frontière est-elle à la bonne place ? */
            const r = ctx.resolue.repere;
            let pire = 0;
            for (let i = 0; i <= 40; i += 1) {
                const x = r.xMin + ((r.xMax - r.xMin) * i) / 40;
                const ya = evaluerFonctionV2(attendue.fonction, x);
                const d = plusProcheSurLigne({ x, y: ya }, o.a, o.b, 'droite').d;
                pire = Math.max(pire, d);
            }
            if (pire > tol.distance) {
                return tranche(false, pire, 0, pire, milieu(o.a, o.b), `La frontière s'écarte de ${nb(pire)} de celle attendue ; ${nb(tol.distance)} au plus.`);
            }
            /* 2. le CÔTÉ, par huit points témoins. On ne compare pas les mots :
               sur une frontière oblique, « au-dessus » et « à droite » peuvent
               désigner le même demi-plan. On regarde donc où tombent des points. */
            const temoins = [];
            for (let i = 0; i < 8; i += 1) {
                const a = (2 * Math.PI * i) / 8;
                temoins.push({
                    x: (r.xMin + r.xMax) / 2 + ((r.xMax - r.xMin) / 2.5) * Math.cos(a),
                    y: (r.yMin + r.yMax) / 2 + ((r.yMax - r.yMin) / 2.5) * Math.sin(a),
                });
            }
            let juges = 0;
            for (const t of temoins) {
                const sAttendu = cotePourPoint(t, fa, fb, coteVise);
                const sEleve = cotePourPoint(t, o.a, o.b, o.cote);
                /* Un témoin trop près de l'une des frontières ne tranche rien. */
                if (Math.abs(sAttendu) <= tol.distance || Math.abs(sEleve) <= tol.distance)
                    continue;
                juges += 1;
                if (sAttendu > 0 !== sEleve > 0) {
                    return tranche(false, null, null, null, t, `Le mauvais côté est hachuré : le point (${nb(t.x)} ; ${nb(t.y)}) ${sAttendu > 0 ? "devrait être dans le demi-plan et n'y est pas" : "y est alors qu'il ne devrait pas"}.`);
                }
            }
            if (juges === 0)
                return indeterminee('Aucun point ne permet de dire quel côté est hachuré.');
            /* 3. stricte ou large : la frontière est-elle dans le demi-plan ? */
            const stricteVisee = c.stricte === true;
            if (o.stricte !== stricteVisee) {
                return tranche(false, null, null, null, milieu(o.a, o.b), stricteVisee
                    ? 'La frontière est tracée pleine ; une inégalité stricte la met en pointillé.'
                    : 'La frontière est tracée en pointillé ; une inégalité large la trace pleine.');
            }
            return tranche(true, pire, 0, pire, milieu(o.a, o.b), `Le demi-plan hachuré est le bon, frontière ${stricteVisee ? 'exclue' : 'comprise'}.`);
        }
        case 'vecteur_egal': {
            const o = obj(c.objet);
            const x = nombreDe(c.x);
            const y = nombreDe(c.y);
            if (o === null || o.genre !== 'vecteur' || x === null || y === null) {
                return indeterminee('Aucun vecteur tracé.');
            }
            const vx = o.a.x - o.de.x;
            const vy = o.a.y - o.de.y;
            const d = Math.hypot(vx - x, vy - y);
            return tranche(d <= tol.distance, d, 0, d, milieu(o.de, o.a), `Le vecteur a pour composantes (${nb(vx)} ; ${nb(vy)}) ; (${nb(x)} ; ${nb(y)}) attendues.`);
        }
        default:
            /* P4 : le validateur les refuse (`griefsAttenduConstruction`). Si l'une
             * passe malgré tout, elle ne prétend pas être mesurée. */
            return indeterminee(`La contrainte « ${c.type} » n'est pas vérifiée à cette phase.`);
    }
}
/** Les noms des sommets d'un polygone de l'élève, dans l'ordre de sa définition. */
function nomsDesSommets(o, ctx) {
    if (o.genre !== 'polygone')
        return null;
    const def = ctx.defs.get(o.id)?.def;
    if (def === undefined || def.type !== 'polygone')
        return null;
    return def.sommets.map((id) => {
        const s = ctx.resolue.objets.get(id);
        return s?.nom === undefined ? '' : normaliserNom(s.nom);
    });
}
/** Deux listes de noms décrivent-elles le même cycle, dans un sens ou dans l'autre ? */
function memeCycle(attendus, obtenus) {
    const n = attendus.length;
    if (n === 0 || obtenus.length !== n)
        return false;
    for (const sens of [1, -1]) {
        for (let depart = 0; depart < n; depart += 1) {
            let tout = true;
            for (let i = 0; i < n && tout; i += 1) {
                tout = attendus[i] === obtenus[(((depart + sens * i) % n) + n) % n];
            }
            if (tout)
                return true;
        }
    }
    return false;
}
/**
 * `image` : l'image EXACTE de la source, calculée par le code, comparée à
 * l'objet de l'élève (§ 5.1). La correspondance des noms, quand elle est
 * donnée, apparie les sommets un à un ; sinon, on prend la meilleure
 * permutation circulaire, dans les deux sens.
 */
function mesurerImage(c, ctx, liaison) {
    const tol = ctx.tol;
    const cible = objetRef(c.objet, ctx, liaison);
    const source = objetRef(c.source, ctx, liaison);
    const f = transformationRef(c.transformation, ctx, liaison);
    if (cible === null || source === null)
        return indeterminee('Aucune image construite.');
    if (f === null)
        return indeterminee("La transformation attendue n'est pas lisible.");
    const corr = c.correspondance;
    if (typeof corr === 'object' && corr !== null && !Array.isArray(corr)) {
        /* Appariement par les NOMS : « A → A′ ». Il vérifie du même coup que
         * l'élève a bien nommé les sommets de son image. */
        let pire = 0;
        let ou = null;
        for (const [de, vers] of Object.entries(corr)) {
            if (typeof vers !== 'string')
                continue;
            const ps = ctx.parNom.get(normaliserNom(de));
            const pi = ctx.parNom.get(normaliserNom(vers));
            if (ps === undefined || ps.genre !== 'point' || pi === undefined || pi.genre !== 'point') {
                return indeterminee(`Le point ${vers} n'est pas placé.`);
            }
            const d = distance(f(ps.p), pi.p);
            if (d > pire) {
                pire = d;
                ou = pi.p;
            }
        }
        return tranche(pire <= tol.distance, pire, 0, pire, ou, `Le sommet le plus écarté de son image est à ${nb(pire)} ; ${nb(tol.distance)} au plus.`);
    }
    /* Sans correspondance : on compare les géométries, genre par genre. */
    if (source.genre === 'point' && cible.genre === 'point') {
        const d = distance(f(source.p), cible.p);
        return tranche(d <= tol.distance, d, 0, d, cible.p, `Le point est à ${nb(d)} de son image ; ${nb(tol.distance)} au plus.`);
    }
    if (source.genre === 'polygone' && cible.genre === 'polygone') {
        const d = ecartCirculaire(source.sommets.map(f), cible.sommets);
        if (d === null)
            return indeterminee("Le polygone n'a pas le même nombre de sommets.");
        return tranche(d <= tol.distance, d, 0, d, cible.sommets[0] ?? null, `Le sommet le plus écarté de son image est à ${nb(d)} ; ${nb(tol.distance)} au plus.`);
    }
    if (source.genre === 'cercle' && cible.genre === 'cercle') {
        const centre = f(source.centre);
        const bord = f({ x: source.centre.x + source.rayon, y: source.centre.y });
        const rayon = distance(centre, bord);
        const dCentre = distance(centre, cible.centre);
        const eRayon = rayon < EPSILON ? 0 : Math.abs(cible.rayon - rayon) / rayon;
        const ok = dCentre <= tol.distance && eRayon <= tol.relative;
        return tranche(ok, dCentre, 0, dCentre, cible.centre, `Le centre est à ${nb(dCentre)} de son image, le rayon en diffère de ${pourcent(eRayon)}.`);
    }
    if (source.genre === 'ligne' && cible.genre === 'ligne') {
        /* Une ligne se compare par DEUX points : les images des points qui la
         * définissent doivent tomber sur la ligne de l'élève. */
        const l = { a: cible.a, b: cible.b, sorte: cible.sorte };
        const d = Math.max(plusProcheSurLigne(f(source.a), l.a, l.b, l.sorte).d, plusProcheSurLigne(f(source.b), l.a, l.b, l.sorte).d);
        return tranche(d <= tol.distance, d, 0, d, milieu(l.a, l.b), `La ligne passe à ${nb(d)} de son image ; ${nb(tol.distance)} au plus.`);
    }
    return indeterminee("L'objet construit n'est pas du même genre que la source.");
}
/**
 * `isometrique` / `semblable` : côtés et angles appariés par la meilleure
 * permutation circulaire, et rapport des côtés CONSTANT (§ 5.1). Une figure
 * semblable sans rapport donné se juge sur la constance seule.
 */
function mesurerSemblable(c, ctx, liaison) {
    const tol = ctx.tol;
    const cible = objetRef(c.polygone ?? c.objet, ctx, liaison);
    const source = objetRef(c.source, ctx, liaison);
    if (cible === null ||
        source === null ||
        cible.genre !== 'polygone' ||
        source.genre !== 'polygone') {
        return indeterminee('Aucun polygone à comparer.');
    }
    const n = source.sommets.length;
    if (n < 3 || cible.sommets.length !== n) {
        return indeterminee("Le polygone n'a pas le même nombre de sommets.");
    }
    const rapportDonne = nombreDe(c.rapport);
    const cotesSource = cotesDe(source.sommets);
    const cotesCible = cotesDe(cible.sommets);
    const anglesSource = anglesDe(source.sommets);
    const anglesCible = anglesDe(cible.sommets);
    const sommeSource = cotesSource.reduce((s, l) => s + l, 0);
    if (sommeSource < EPSILON)
        return indeterminee('Le polygone de départ est dégénéré.');
    /* Le rapport : celui qu'on impose, celui de l'isométrie (1), ou celui que
     * les périmètres donnent — c'est alors sa CONSTANCE qu'on mesure. */
    const k = rapportDonne !== null
        ? rapportDonne
        : c.type === 'isometrique'
            ? 1
            : cotesCible.reduce((s, l) => s + l, 0) / sommeSource;
    if (!(Math.abs(k) > EPSILON))
        return indeterminee('Le rapport est nul.');
    let pireCote = Infinity;
    let pireAngle = Infinity;
    for (const sens of [1, -1]) {
        for (let depart = 0; depart < n; depart += 1) {
            let cote = 0;
            let angle = 0;
            for (let i = 0; i < n; i += 1) {
                const j = (((depart + sens * i) % n) + n) % n;
                const attendu = cotesSource[i] * k;
                const obtenu = cotesCible[sens === 1 ? j : (((j - 1) % n) + n) % n];
                cote = Math.max(cote, Math.abs(attendu) < EPSILON ? 0 : Math.abs(obtenu - attendu) / Math.abs(attendu));
                angle = Math.max(angle, Math.abs(anglesCible[j] - anglesSource[i]));
            }
            if (cote + angle < pireCote + pireAngle) {
                pireCote = cote;
                pireAngle = angle;
            }
        }
    }
    const ok = pireCote <= tol.relative && pireAngle <= tol.angleDeg;
    const quoi = c.type === 'isometrique' ? 'isométrique' : 'semblable';
    return tranche(ok, k, rapportDonne, pireCote, cible.sommets[0] ?? null, `Rapport des côtés ${nb(k)} (écart de ${pourcent(pireCote)}), angles écartés de ${nb(pireAngle)}° au plus ; figure ${quoi} attendue.`);
}
/* ================================================================ les liaisons */
/** Les cibles qu'une contrainte met en jeu — y compris dans une transformation. */
function ciblesDe(c) {
    const sortie = new Set();
    const visiter = (v) => {
        if (Array.isArray(v)) {
            v.forEach(visiter);
            return;
        }
        if (typeof v !== 'object' || v === null)
            return;
        const o = v;
        if (typeof o.cible === 'string') {
            sortie.add(o.cible);
            return;
        }
        Object.values(o).forEach(visiter);
    };
    /* `nomme` et `style` citent leur cible par une chaîne nue ; les autres par
     * une référence `{ cible: 'X1' }`. */
    if (typeof c.cible === 'string')
        sortie.add(c.cible);
    for (const [cle, v] of Object.entries(c)) {
        if (cle !== 'cible' && cle !== 'type')
            visiter(v);
    }
    return [...sortie];
}
/** L'objet de l'élève répond-il au genre demandé par la cible ? */
function correspondAuGenre(o, genre) {
    switch (genre) {
        case 'ligne':
            return o.genre === 'ligne';
        case 'segment':
        case 'droite':
        case 'demi_droite':
            return o.genre === 'ligne' && o.sorte === genre;
        case 'point':
            return o.genre === 'point';
        case 'cercle':
            return o.genre === 'cercle';
        case 'polygone':
            return o.genre === 'polygone';
        case 'fonction':
        case 'par_parties':
            return o.genre === 'fonction';
        /* L6 (F8) : l'esquisse de l'élève — ou, pour la référence rejouée, sa fonction. */
        case 'esquisse':
            return (o.genre === 'marque' && o.sorte === 'esquisse') || o.genre === 'fonction';
        case 'vecteur':
            return o.genre === 'vecteur';
        case 'demi_plan':
            return o.genre === 'demi_plan';
        case 'marque':
            return o.genre === 'marque';
        default:
            /* solide, developpement : phase 4. */
            return false;
    }
}
/** Au-delà, on renonce au produit cartésien et on lie cible par cible. */
const ESSAIS_MAX = 4096;
/**
 * Trouver l'objet de l'élève qui répond à chaque cible (§ 5.3).
 *
 *  1. une cible NOMMÉE se lie à l'objet de même genre et de même nom ;
 *  2. sinon, parmi les objets de son genre, à celui qui tient le plus de
 *     contraintes de cette cible ; à égalité, le plus RÉCENT ;
 *  3. aucun objet du genre : la cible n'est liée à rien, et ses contraintes
 *     seront `indeterminee`.
 *
 * Deux cibles liées par une même contrainte (« le point est sur la droite »)
 * se cherchent ENSEMBLE : les prendre l'une après l'autre choisirait un point
 * qui ne va pas avec la droite retenue. Le produit reste petit — au plus 4
 * cibles par partie.
 */
function lier(attendu, eleve, ctx) {
    const liaison = new Map();
    const cibles = attendu.cibles;
    for (const c of cibles)
        liaison.set(c.id, null);
    /** Les candidats de chaque cible, du plus ancien au plus récent. */
    const candidats = new Map();
    for (const cible of cibles) {
        const duGenre = eleve.filter((o) => correspondAuGenre(o, cible.genre));
        if (cible.nom !== undefined) {
            const vise = normaliserNom(cible.nom);
            const nommes = duGenre.filter((o) => o.nom !== undefined && normaliserNom(o.nom) === vise);
            /* Aucun objet ne porte le nom : on cherche quand même, et `nomme` dira
             * que le nom manque. La construction peut être juste, l'écriture non. */
            candidats.set(cible.id, nommes.length > 0 ? nommes : duGenre);
        }
        else
            candidats.set(cible.id, duGenre);
    }
    /* Les groupes : les cibles reliées par une contrainte commune. */
    const groupe = new Map();
    cibles.forEach((c, i) => groupe.set(c.id, i));
    const fusionner = (a, b) => {
        const ga = groupe.get(a);
        const gb = groupe.get(b);
        if (ga === undefined || gb === undefined || ga === gb)
            return;
        for (const [cle, g] of groupe)
            if (g === gb)
                groupe.set(cle, ga);
    };
    for (const c of attendu.contraintes) {
        const liees = ciblesDe(c).filter((x) => groupe.has(x));
        for (let i = 1; i < liees.length; i += 1) {
            fusionner(liees[0], liees[i]);
        }
    }
    /** Le rang de création : plus il est grand, plus l'objet est récent. */
    const rang = new Map();
    eleve.forEach((o, i) => rang.set(o.id, i));
    for (const tete of new Set(groupe.values())) {
        const membres = cibles.filter((c) => groupe.get(c.id) === tete).map((c) => c.id);
        const contraintes = attendu.contraintes.filter((c) => ciblesDe(c).some((x) => membres.includes(x)));
        const listes = membres.map((m) => candidats.get(m) ?? []);
        const total = listes.reduce((p, l) => p * Math.max(1, l.length), 1);
        /** Le score d'une affectation : contraintes tenues, puis fraîcheur. */
        const noter = (essai) => {
            const complet = new Map(liaison);
            for (const [cle, v] of essai)
                complet.set(cle, v);
            let tenues = 0;
            for (const c of contraintes) {
                if (mesurer(c, ctx, complet).etat === 'tenue')
                    tenues += 1;
            }
            let fraicheur = 0;
            for (const v of essai.values())
                fraicheur += v === null ? -1 : (rang.get(v.id) ?? 0);
            return [tenues, fraicheur];
        };
        let meilleur = null;
        let note = [-1, -1];
        if (total <= ESSAIS_MAX) {
            /* Produit cartésien : chaque combinaison de candidats est essayée. */
            const essayer = (i, courant) => {
                if (i === membres.length) {
                    const n = noter(courant);
                    if (n[0] > note[0] || (n[0] === note[0] && n[1] > note[1])) {
                        note = n;
                        meilleur = new Map(courant);
                    }
                    return;
                }
                const cle = membres[i];
                const liste = listes[i];
                if (liste.length === 0) {
                    courant.set(cle, null);
                    essayer(i + 1, courant);
                    return;
                }
                for (const o of liste) {
                    courant.set(cle, o);
                    essayer(i + 1, courant);
                }
            };
            essayer(0, new Map());
        }
        else {
            /* Garde-fou : trop de combinaisons, on lie cible par cible. */
            const courant = new Map();
            membres.forEach((cle, i) => {
                const liste = listes[i];
                courant.set(cle, liste.length === 0 ? null : liste[liste.length - 1]);
            });
            meilleur = courant;
        }
        if (meilleur !== null)
            for (const [cle, v] of meilleur)
                liaison.set(cle, v);
    }
    return liaison;
}
/* ================================================================ le verdict */
/**
 * Des constats au verdict (§ 5.4). C'est le barème existant
 * (`POINTS_PAR_VERDICT` : juste 1, à écrire 1, partiel 0,5).
 */
export function verdictDeConstats(constats, aucunObjet) {
    if (aucunObjet)
        return 'pas_fait';
    const requis = constats.filter((c) => !c.forme);
    const tenues = requis.filter((c) => c.etat === 'tenue').length;
    /* Une contrainte d'ÉCRITURE non tenue ne fait jamais descendre à `partiel` :
     * elle donne `a_ecrire`, qui vaut le point entier (décision AD-V2-02). */
    const ecritureARevoir = constats.some((c) => c.forme && c.etat !== 'tenue');
    if (tenues === requis.length)
        return ecritureARevoir ? 'a_ecrire' : 'juste';
    if (tenues === 0)
        return 'erreur';
    return 'partiel';
}
/* ================================================================ le contrôle de l'attendu */
/**
 * Les défauts de FORME d'un attendu — ce que le validateur de la génération
 * rejoue (§ 7.2). Une contrainte d'une phase non livrée est REFUSÉE : elle
 * passerait sinon en `indeterminee`, et une banque entière serait notée sur
 * des mesures que personne ne fait.
 */
export function griefsAttenduConstruction(attendu, phase = PHASE_VERIFIEE) {
    const g = [];
    const ids = new Set();
    if (!Array.isArray(attendu?.cibles) || attendu.cibles.length === 0) {
        g.push('attendu : aucune cible');
    }
    else {
        attendu.cibles.forEach((c, i) => {
            if (typeof c?.id !== 'string' || c.id === '')
                g.push(`cible ${i + 1} : identifiant absent`);
            else if (ids.has(c.id))
                g.push(`cible ${i + 1} : identifiant « ${c.id} » déjà pris`);
            else
                ids.add(c.id);
        });
    }
    if (!Array.isArray(attendu?.contraintes) || attendu.contraintes.length === 0) {
        g.push('attendu : aucune contrainte');
        return g;
    }
    attendu.contraintes.forEach((c, i) => {
        const quoi = `contrainte ${i + 1}`;
        const type = c?.type;
        if (!estTypeContrainte(type)) {
            g.push(`${quoi} : « ${String(type)} » n'est pas au catalogue`);
            return;
        }
        if (PHASE_CONTRAINTE[type] > phase) {
            g.push(`${quoi} : « ${type} » est réservée à la phase ${PHASE_CONTRAINTE[type]}`);
        }
        for (const cible of ciblesDe(c)) {
            if (!ids.has(cible))
                g.push(`${quoi} (${c.type}) : cite « ${cible} », qui n'est pas une cible`);
        }
    });
    return g;
}
/* ================================================================ le vérificateur */
/**
 * Vérifie une construction contre l'attendu de la banque (§ 5).
 *
 * `enonce` est la figure de l'énoncé RELUE PAR LE SERVEUR (§ 1.1, principe
 * 3) : jamais celle que l'application envoie. `attendu` ne quitte jamais la
 * banque.
 *
 * Le document est résolu à la phase 2 : le vérificateur doit savoir lire une
 * médiatrice ou une image même si la barre d'outils ne les propose pas encore
 * (voir `OptionsResolution` dans `construction.ts`).
 */
export function verifierConstruction(doc, enonce, attendu) {
    const resolue = resoudreConstruction(doc, enonce, { phase: PHASE_VERIFIEE });
    /* Les objets de l'ÉLÈVE, dans l'ordre de création. Un objet masqué compte
     * (un trait de construction reste une construction) ; un objet indéfini,
     * non (§ 5.3). */
    const defs = new Map();
    const eleve = [];
    for (const o of Array.isArray(doc?.objets) ? doc.objets : []) {
        defs.set(o.id, o);
        const r = resolue.objets.get(o.id);
        if (r !== undefined && r.genre !== 'indefini')
            eleve.push(r);
    }
    /* Les points nommés, énoncé PUIS élève : c'est ce que lit la correspondance
     * de noms d'une image (« A → A′ »). */
    const parNom = new Map();
    for (const o of resolue.objets.values()) {
        if (o.genre === 'point' && o.nom !== undefined) {
            const cle = normaliserNom(o.nom);
            if (!parNom.has(cle))
                parNom.set(cle, o);
        }
    }
    const ctx = {
        resolue,
        tol: toleranceDe(resolue.repere, attendu?.tolerance),
        defs,
        parNom,
    };
    const contraintes = Array.isArray(attendu?.contraintes) ? attendu.contraintes : [];
    const liaison = lier({ ...attendu, contraintes, cibles: attendu?.cibles ?? [] }, eleve, ctx);
    const constats = contraintes.map((c, i) => {
        const m = mesurer(c, ctx, liaison);
        /* L'objet du constat : la première cible que la contrainte met en jeu. */
        const premiere = ciblesDe(c)[0];
        const objet = premiere === undefined ? null : (liaison.get(premiere) ?? null);
        return {
            contrainte: i,
            type: c.type,
            etat: m.etat,
            forme: c.forme === true,
            mesure: m.mesure ?? null,
            attendu: m.attendu ?? null,
            ecart: m.ecart ?? null,
            ou: m.ou ?? null,
            objet: objet?.id ?? null,
            construitPar: objet === null ? null : (defs.get(objet.id)?.def.type ?? null),
            texte: m.texte,
        };
    });
    const verdictMesure = verdictDeConstats(constats, eleve.length === 0);
    /* La MÉTHODE imposée (arbitrage A2) : l'élève garde tous ses outils, mais
     * si le vérificateur constate qu'il a employé l'outil interdit, il le DIT,
     * et le verdict descend à « partiel ». On regarde TOUT le document : un
     * trait de construction masqué reste une façon de faire. */
    const exclus = new Set(attendu?.methode?.outilsExclus ?? []);
    const methode = [];
    if (exclus.size > 0) {
        for (const o of defs.values()) {
            const type = o.def?.type;
            const outil = type === undefined ? null : OUTIL_PAR_DEFINITION[type];
            if (outil !== null && outil !== undefined && exclus.has(outil)) {
                methode.push({ objet: o.id, outil });
            }
        }
    }
    /* Un outil interdit ne rend jamais une construction fausse : il plafonne. */
    const verdict = methode.length > 0 && (verdictMesure === 'juste' || verdictMesure === 'a_ecrire')
        ? 'partiel'
        : verdictMesure;
    const liaisons = {};
    for (const [cle, o] of liaison)
        liaisons[cle] = o?.id ?? null;
    return { constats, liaisons, verdict, methode };
}
//# sourceMappingURL=verifier.js.map